import axios, { AxiosInstance } from 'axios'
import moment from 'moment'
import { inject } from 'vue'
import { Store } from 'vuex'
import { focusElementWithError } from './common'
import { Api, SessionCache, GlobalState } from './types'

export const apiSymbol = Symbol('api')
export const useApi = () => inject(apiSymbol) as Api

function createAxios(): AxiosInstance {
  const axi = axios.create({
    baseURL: process.env.VUE_APP_BASE_URI,
    withCredentials: true,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=-1,private',
      Expires: '-1',
      'X-Xsrf-Token': 'true'
    }
  })
  axi.interceptors.response.use(
    function (response) {
      return response
    },
    function (error) {
      if (error.response.status === 401) {
        //if (store.state.value.user.authenticated) {
        // pazi!
        window.location.reload()
        //}
      }
      return Promise.reject(error)
    }
  )
  return axi
}

function createCache(axi: AxiosInstance): SessionCache {
  let cache: Array<any> = []

  const clear = (key: any) => {
    if (key) {
      cache[key] = null
    } else {
      cache = []
    }
  }

  const get = (key: any, url?: string, minutes?: number, action?: string, params?: any) => {
    if (!url || url === '') {
      url = key
    }

    minutes = minutes ?? 5
    action = action ?? 'get'

    const el = cache[key]
    if (el && new Date() < el.expire) {
      return el.data
    }

    const data = action === 'post' ? axi.post(url as string, params) : axi.get(url as string)
    cache[key] = {
      data: data,
      expire: moment().add(minutes, 'minute').toDate()
    }
    return data
  }

  const sc = {
    clear,
    get
  }
  return sc
}

export function createApi(store: Store<GlobalState>) {
  const axi = createAxios()

  const get = async (url: string, setLoading = true) => {
    let res = null
    if (setLoading) {
      store.commit('appLoading', true)
    }
    try {
      res = await axi.get(url)
    } catch (e) {
      res = e.response
      store.commit('appErrors', e.response.data)
    } finally {
      if (setLoading) {
        store.commit('appLoading', false)
      }
    }
    return res
  }

  const post = async (url: string, data: any, setLoading = true) => {
    let res = {}
    if (setLoading) {
      store.commit('appLoading', true)
    }
    try {
      store.commit('appErrors', [])
      res = await axi.post(url, data)
    } catch (e) {
      res = e.response
      store.commit('appErrors', e.response.data)
      focusElementWithError()
    } finally {
      if (setLoading) {
        store.commit('appLoading', false)
      }
    }
    return res
  }

  const del = async (url: string, setLoading = true) => {
    let res = null
    if (setLoading) {
      store.commit('appLoading', true)
    }
    try {
      res = await axi.delete(url)
    } catch (e) {
      res = e.response
      store.commit('appErrors', e.response.data)
    } finally {
      if (setLoading) {
        store.commit('appLoading', false)
      }
    }
    return res
  }

  const api = {
    axios: axi,
    cache: createCache(axi),
    get,
    post,
    delete: del
  }

  return api as Api
}
